import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import UserGeneralDialogContent from './UserGeneralDialogContent';
import { userFormSchema } from '../../helper/helper';
import employeeService from '../../../../services/EmployeeService';

export default function UserGeneral({
  row,
  onChange,
  onCancelChanges,
  onApplyChanges,
  vendorRecords,
  keyPayEmployeeRecords,
  selectedEmployeeToRegister,
  setSelectedEmployeeToRegister,
  selectedUserType,
  setSelectedUserType,
  approvalRequired,
  setApprovalRequired,
  selectedApprover,
  setSelectedApprover,
  open,
  portalAccess,
  setPortalAccess,
  portalAccessDialog,
  setPortalAccessDialog,
  setRows,
  rows
}) {
  const defaultValues = useMemo(
    () => ({
      ...selectedEmployeeToRegister,
      ...row,
      email: row?.email ?? selectedEmployeeToRegister?.email ?? '',
      workEmail: row?.workEmail ?? selectedEmployeeToRegister?.workEmail ?? '',
      PrimaryPhone: row?.PrimaryPhone ?? selectedEmployeeToRegister?.PrimaryPhone ?? '',
      userRole: selectedUserType?.value || row?.userRole || row?.employeeType || '',
      approvalRequired: Boolean(approvalRequired),
      approverId: selectedApprover?.id || ''
    }),
    // Reset only when a different user/source employee is loaded, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [row?.id, selectedEmployeeToRegister]
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    trigger,
    formState: { errors, dirtyFields, isSubmitting, isValid }
  } = useForm({
    defaultValues,
    resolver: zodResolver(userFormSchema),
    mode: 'onChange'
  });
  const workEmail = useWatch({ control, name: 'workEmail' });
  const [outlookEmailStatus, setOutlookEmailStatus] = useState('idle');
  const originalWorkEmail = useRef('');

  useEffect(() => {
    if (open) originalWorkEmail.current = defaultValues.workEmail?.trim().toLowerCase() || '';
    // Capture once when this user's dialog opens. The `row` prop changes while typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row?.id]);

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      trigger();
    }
  }, [defaultValues, open, reset, trigger]);

  useEffect(() => {
    setValue('userRole', selectedUserType?.value || '', { shouldValidate: true });
  }, [selectedUserType?.value, setValue]);

  useEffect(() => {
    const normalizedEmail = workEmail?.trim().toLowerCase() || '';
    const existingEmail = originalWorkEmail.current;
    const hasValidFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail);

    if (!open || !normalizedEmail || !hasValidFormat) {
      setOutlookEmailStatus('idle');
      return undefined;
    }

    if (row?.id && normalizedEmail === existingEmail) {
      setOutlookEmailStatus('valid');
      return undefined;
    }

    let active = true;
    setOutlookEmailStatus('checking');
    clearErrors('workEmail');

    // eslint-disable-next-line no-undef
    const timer = window.setTimeout(async () => {
      try {
        const response = await employeeService.validateOutlookEmail(normalizedEmail);
        if (!active) return;

        if (response?.data?.exists) {
          setOutlookEmailStatus('valid');
          clearErrors('workEmail');
          await trigger('workEmail');
        } else {
          setOutlookEmailStatus('invalid');
          setError('workEmail', {
            type: 'outlook',
            message: 'No user exists with the provided email address.'
          });
        }
      } catch (error) {
        if (!active) return;
        setOutlookEmailStatus('error');
        setError('workEmail', {
          type: 'outlook',
          message:
            error?.response?.data?.message || 'Unable to verify this email with Microsoft Outlook. Please try again.'
        });
      }
    }, 500);

    return () => {
      active = false;
      // eslint-disable-next-line no-undef
      window.clearTimeout(timer);
    };
  }, [clearErrors, open, row?.id, setError, trigger, workEmail]);

  const submitForm = handleSubmit(async (values) => {
    try {
      await onApplyChanges(values, dirtyFields);
    } catch (error) {
      const field = error?.response?.data?.field;
      if (field) {
        if (field === 'workEmail') {
          setOutlookEmailStatus(error?.response?.data?.code === 'OUTLOOK_USER_NOT_FOUND' ? 'invalid' : 'error');
        }
        setError(field, {
          type: 'server',
          message: error?.response?.data?.message || 'Could not validate this field.'
        });
      }
    }
  });
  const requiresOutlookVerification = Boolean(workEmail?.trim());

  return (
    <div style={{ height: '800px' }}>
      <DialogContent>
        <UserGeneralDialogContent
          row={row}
          onChange={onChange}
          vendorRecords={vendorRecords}
          keyPayEmployeeRecords={keyPayEmployeeRecords}
          selectedEmployeeToRegister={selectedEmployeeToRegister}
          setSelectedEmployeeToRegister={setSelectedEmployeeToRegister}
          selectedUserType={selectedUserType}
          setSelectedUserType={setSelectedUserType}
          approvalRequired={approvalRequired}
          setApprovalRequired={setApprovalRequired}
          selectedApprover={selectedApprover}
          setSelectedApprover={setSelectedApprover}
          open={open}
          portalAccessDialog={portalAccessDialog}
          setPortalAccessDialog={setPortalAccessDialog}
          portalAccess={portalAccess}
          setPortalAccess={setPortalAccess}
          setRows={setRows}
          rows={rows}
          control={control}
          errors={errors}
          setFormValue={setValue}
          outlookEmailStatus={outlookEmailStatus}
        />
      </DialogContent>
      <DialogActions style={{ padding: '0 23px 16px 0' }}>
        <Button onClick={onCancelChanges} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={submitForm}
          variant="contained"
          color="primary"
          disabled={
            isSubmitting ||
            !isValid ||
            (requiresOutlookVerification && (!row?.id || dirtyFields.workEmail) && outlookEmailStatus !== 'valid')
          }
        >
          {isSubmitting ? 'Saving…' : row?.id ? 'Save changes' : 'Create user'}
        </Button>
      </DialogActions>
    </div>
  );
}

UserGeneral.propTypes = {
  row: PropTypes.any,
  onChange: PropTypes.func,
  onCancelChanges: PropTypes.func,
  onApplyChanges: PropTypes.func,
  vendorRecords: PropTypes.array,
  keyPayEmployeeRecords: PropTypes.array,
  selectedEmployeeToRegister: PropTypes.any,
  setSelectedEmployeeToRegister: PropTypes.func,
  selectedUserType: PropTypes.shape({ value: PropTypes.string, label: PropTypes.string }),
  setSelectedUserType: PropTypes.func,
  approvalRequired: PropTypes.bool,
  setApprovalRequired: PropTypes.func,
  selectedApprover: PropTypes.any,
  setSelectedApprover: PropTypes.func,
  open: PropTypes.bool,
  portalAccess: PropTypes.bool,
  setPortalAccess: PropTypes.func,
  portalAccessDialog: PropTypes.bool,
  setPortalAccessDialog: PropTypes.func,
  setRows: PropTypes.func,
  rows: PropTypes.any
};
